import { Routes, Route } from 'react-router-dom'
import Layout from './components/Layout'
import AdminLayout from './components/AdminLayout'
import PublisherLayout from './components/PublisherLayout'
import ProtectedRoute from './components/ProtectedRoute'
import ProtectedPublisherRoute from './components/ProtectedPublisherRoute'

// Public pages
import HomePage from './pages/HomePage'
import ComingSoonPage from './pages/ComingSoonPage'
import BetaAccessPage from './pages/BetaAccessPage'
import BrowsePage from './pages/BrowsePage'
import ToolDetailPage from './pages/ToolDetailPage'
import CreatorPage from './pages/CreatorPage'
import DocsPage from './pages/DocsPage'
import DownloadsPage from './pages/DownloadsPage'
import CollectionsPage from './pages/CollectionsPage'
import CollectionDetailPage from './pages/CollectionDetailPage'
import NotFoundPage from './pages/NotFoundPage'

// Admin pages
import AdminLoginPage from './pages/admin/AdminLoginPage'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import AdminReviewsPage from './pages/admin/AdminReviewsPage'
import AdminPublishersPage from './pages/admin/AdminPublishersPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminWaitlistPage from './pages/admin/AdminWaitlistPage'
import AdminBetaTokensPage from './pages/admin/AdminBetaTokensPage'

// Publisher pages
import PublisherLoginPage from './pages/publisher/PublisherLoginPage'
import PublisherSignupPage from './pages/publisher/PublisherSignupPage'
import PublisherDashboardPage from './pages/publisher/PublisherDashboardPage'
import PublisherPackagesPage from './pages/publisher/PublisherPackagesPage'
import PublisherNewPackagePage from './pages/publisher/PublisherNewPackagePage'
import PublisherPackageSettingsPage from './pages/publisher/PublisherPackageSettingsPage'
import PublisherCollectionsPage from './pages/publisher/PublisherCollectionsPage'
import PublisherSettingsPage from './pages/publisher/PublisherSettingsPage'

// Check if coming soon mode is enabled
const isComingSoonMode = import.meta.env.VITE_COMING_SOON_MODE === 'true'

function App() {
  // In coming soon mode, show the coming soon page as the home page
  // but still allow access to admin, beta portal, and publisher routes
  if (isComingSoonMode) {
    return (
      <Routes>
        {/* Coming soon page as home */}
        <Route path="/" element={<ComingSoonPage />} />

        {/* Beta access portal - gated entry to full platform */}
        <Route path="/beta" element={<BetaAccessPage />} />

        {/* Admin routes still accessible */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="reviews" element={<AdminReviewsPage />} />
          <Route path="publishers" element={<AdminPublishersPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="waitlist" element={<AdminWaitlistPage />} />
          <Route path="beta-tokens" element={<AdminBetaTokensPage />} />
        </Route>

        {/* Publisher signup/login accessible for beta testers */}
        <Route path="/publisher/signup" element={<PublisherSignupPage />} />
        <Route path="/publisher/login" element={<PublisherLoginPage />} />

        {/* Full publisher dashboard accessible for beta testers with valid publisher token */}
        <Route
          path="/publisher"
          element={
            <ProtectedPublisherRoute>
              <PublisherLayout />
            </ProtectedPublisherRoute>
          }
        >
          <Route index element={<PublisherDashboardPage />} />
          <Route path="packages" element={<PublisherPackagesPage />} />
          <Route path="packages/new" element={<PublisherNewPackagePage />} />
          <Route path="packages/:packageId" element={<PublisherPackageSettingsPage />} />
          <Route path="packages/:packageId/settings" element={<PublisherPackageSettingsPage />} />
          <Route path="collections" element={<PublisherCollectionsPage />} />
          <Route path="settings" element={<PublisherSettingsPage />} />
        </Route>

        {/* Full application routes accessible via beta portal */}
        <Route path="/browse" element={<Layout />}>
          <Route index element={<BrowsePage />} />
        </Route>
        <Route path="/skills/:slug" element={<Layout />}>
          <Route index element={<ToolDetailPage />} />
        </Route>
        <Route path="/creators/:publisherId" element={<Layout />}>
          <Route index element={<CreatorPage />} />
        </Route>
        <Route path="/collections" element={<Layout />}>
          <Route index element={<CollectionsPage />} />
        </Route>
        <Route path="/collections/:collectionId" element={<Layout />}>
          <Route index element={<CollectionDetailPage />} />
        </Route>
        <Route path="/docs" element={<Layout />}>
          <Route index element={<DocsPage />} />
          <Route path=":section" element={<DocsPage />} />
        </Route>
        <Route path="/downloads" element={<Layout />}>
          <Route index element={<DownloadsPage />} />
        </Route>

        {/* All other routes redirect to coming soon */}
        <Route path="*" element={<ComingSoonPage />} />
      </Routes>
    )
  }

  // Normal mode - full application
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="browse" element={<BrowsePage />} />
        <Route path="skills/:slug" element={<ToolDetailPage />} />
        <Route path="creators/:publisherId" element={<CreatorPage />} />
        <Route path="collections" element={<CollectionsPage />} />
        <Route path="collections/:collectionId" element={<CollectionDetailPage />} />
        <Route path="docs" element={<DocsPage />} />
        <Route path="docs/:section" element={<DocsPage />} />
        <Route path="downloads" element={<DownloadsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Publisher routes */}
      <Route path="/publisher/login" element={<PublisherLoginPage />} />
      <Route path="/publisher/signup" element={<PublisherSignupPage />} />
      <Route
        path="/publisher"
        element={
          <ProtectedPublisherRoute>
            <PublisherLayout />
          </ProtectedPublisherRoute>
        }
      >
        <Route index element={<PublisherDashboardPage />} />
        <Route path="packages" element={<PublisherPackagesPage />} />
        <Route path="packages/new" element={<PublisherNewPackagePage />} />
        <Route path="packages/:packageId" element={<PublisherPackageSettingsPage />} />
        <Route path="packages/:packageId/settings" element={<PublisherPackageSettingsPage />} />
        <Route path="settings" element={<PublisherSettingsPage />} />
      </Route>

      {/* Admin routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="reviews" element={<AdminReviewsPage />} />
        <Route path="publishers" element={<AdminPublishersPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="waitlist" element={<AdminWaitlistPage />} />
        <Route path="beta-tokens" element={<AdminBetaTokensPage />} />
      </Route>
    </Routes>
  )
}

export default App
